import { localStorage as safeLocalStorage } from 'utils/browserStorage';
export type FeedbackIdentity = { jobId: number; locationId: number; hygienistId: number };
export type FeedbackDelivery = { status: 'pending' | 'accepted' | 'failed' | 'unknown'; eventId: string };
export type AcceptedFeedback = FeedbackIdentity & {
  accepted: true;
  overallRating: number | null;
  supportDelivery?: FeedbackDelivery | null;
  reviewAsk?: { solicitationId: string; [key: string]: unknown };
};

export type FeedbackActor = { key: string; hygienistId: number };

function feedbackActor(value: unknown): FeedbackActor | null {
  const user = value as { id?: unknown; hygienistId?: unknown; dentistId?: unknown; isAdmin?: unknown } | null;
  const hygienistId = Number(user?.hygienistId);
  const userId = Number(user?.id);
  if (!user || user.isAdmin || Number(user.dentistId) > 0
      || !Number.isSafeInteger(hygienistId) || hygienistId <= 0
      || !Number.isSafeInteger(userId) || userId <= 0) return null;
  return { key: `${userId}:${hygienistId}`, hygienistId };
}

/** All available session mirrors must identify the same exclusive Talent. */
export function currentFeedbackActor(...reduxUser: [] | [unknown]): FeedbackActor | null {
  try {
    const getLegacyUser = (window as any).TempStars?.User?.getCurrentUser;
    const stored = feedbackActor(JSON.parse(safeLocalStorage.getItem('userAccount') || 'null'));
    if (!stored) return null;
    if (typeof getLegacyUser === 'function' && feedbackActor(getLegacyUser())?.key !== stored.key) return null;
    if (reduxUser.length && feedbackActor(reduxUser[0])?.key !== stored.key) return null;
    return stored;
  } catch { return null; }
}

function matchingIdentity(value: unknown, identity: FeedbackIdentity): boolean {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return Number(row.jobId) === identity.jobId && Number(row.locationId) === identity.locationId
    && Number(row.hygienistId) === identity.hygienistId;
}

export function acceptedFeedback(value: unknown, identity: FeedbackIdentity): AcceptedFeedback | null {
  if (!matchingIdentity(value, identity)) return null;
  const review = value as Record<string, unknown>;
  if (review.accepted !== true || Number(review.submissionStatus) !== 1) return null;
  // Overall-only (including two-star) reviews are valid. Older category-only
  // submitted feedback is also immutable when the owned API confirms it.
  const overall = Number(review.overallRating);
  const hasOverall = Number.isFinite(overall) && overall > 0 && overall <= 5;
  const hasDetail = ['friendlinessRating', 'infectionControlRating', 'equipmentRating', 'preparationRating', 'collaborationPreference']
    .some(key => Number(review[key]) > 0)
    || (typeof review.additionalFeedback === 'string' && !!review.additionalFeedback.trim());
  if (!hasOverall && !hasDetail) return null;
  return value as AcceptedFeedback;
}

export function definitelyUnsubmitted(error: unknown, identity: FeedbackIdentity): boolean {
  if (!error || typeof error !== 'object') return false;
  const response = error as { status?: number; body?: unknown };
  const body = response.body as { code?: unknown } | undefined;
  return response.status === 404 && body?.code === 'REVIEW_NOT_SUBMITTED'
    && matchingIdentity(body, identity);
}

export function feedbackDeliveryMessage(delivery?: FeedbackDelivery | null): string | null {
  if (!delivery || delivery.status === 'accepted') return null;
  if (delivery.status === 'pending') return 'Your feedback is saved. Your support report is waiting to be delivered. You can continue.';
  if (delivery.status === 'failed') return 'Your feedback is saved, but your support report could not be delivered. You can continue without submitting your feedback again.';
  return 'Your feedback is saved. Delivery of your support report has not been confirmed. You can continue without submitting your feedback again.';
}
